import React, { useState, useRef, useEffect } from 'react';
import { Printer, X, Edit, Save } from 'lucide-react';
import { numberToWords } from '../../utils/numberToWords';


const EditableField = ({ name, value, isEditing, isTextArea = false, onChange }) => {
    if (isEditing) {
        if (isTextArea) {
            return (
                <textarea
                    name={name}
                    value={value}
                    onChange={onChange}
                    style={{
                        border: '1px dashed #ccc',
                        width: '100%',
                        minHeight: '60px',
                        padding: '4px',
                        fontFamily: 'inherit',
                        fontSize: 'inherit',
                        fontWeight: 'inherit',
                        textTransform: 'inherit'
                    }}
                />
            );
        }
        return (
            <input
                type="text"
                name={name}
                value={value}
                onChange={onChange}
                style={{
                    border: '1px dashed #ccc',
                    width: '100%',
                    padding: '2px',
                    fontFamily: 'inherit',
                    fontSize: 'inherit',
                    fontWeight: 'inherit'
                }}
            />
        );
    }
    return <span style={{ whiteSpace: 'pre-wrap' }}>{value}</span>;
};

const FacturePrint = ({ reservation = {}, client = {}, vehicle = {}, withTVA = false, onClose = () => { } }) => {
    const [isEditing, setIsEditing] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const printContentRef = useRef(null);

    const [formData, setFormData] = useState({
        facture_number: `011/2025`,
        facture_date: new Date().toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' }),
        client_name: '',
        client_address: '',
        designation: '',
        unit: 'U',
        quantity: '1',
        pu: '0',
        total_ht: '0',
        tva_rate: '19',
        tva_amount: '0',
        total_ttc: '0',
        amount_in_words: '',
    });

    useEffect(() => {
        const calculateData = () => {
            const days = reservation.total / (reservation.prix_base || 1) || 1;
            const prixBaseRaw = reservation.prix_base;
            const pu = typeof prixBaseRaw === 'number' ? prixBaseRaw : parseFloat(prixBaseRaw) || 0;
            const quantityVal = Math.ceil(days);
            const total_ht = pu * quantityVal;

            let tva_amount = 0;
            let total_ttc = total_ht;

            if (withTVA) {
                tva_amount = total_ht * 0.19;
                total_ttc = total_ht + tva_amount;
            }

            const formatDate = (dateStr) => {
                if (!dateStr) return '';
                return new Date(dateStr).toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' });
            };

            const date_depart = formatDate(reservation.date_depart);
            const date_retour = formatDate(reservation.date_retour_prevue);

            setFormData(prev => ({
                ...prev,
                facture_number: `${reservation.id || '011'}/${new Date().getFullYear()}`,
                client_name: client ? `${client.nom} ${client.prenom}` : '',
                client_address: client?.adresse ? client.adresse.toUpperCase() : 'GHARDAIA MISE A DISPOSITION',
                designation: `Location de voiture du ${date_depart} au ${date_retour}`,
                pu: pu.toFixed(2),
                quantity: `${quantityVal} jours`,
                total_ht: total_ht.toFixed(2),
                tva_amount: tva_amount.toFixed(2),
                total_ttc: total_ttc.toFixed(2),
                amount_in_words: numberToWords(total_ttc),
            }));
            setIsLoading(false);
        };

        calculateData();
    }, [reservation, client, vehicle, withTVA]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => {
            const newData = { ...prev, [name]: value };

            // Recalculate if PU or Quantity changes
            if (name === 'pu' || name === 'quantity') {
                const puStr = String(newData.pu).replace(',', '.');
                const pu = parseFloat(puStr) || 0;

                // Extract number from quantity (e.g., "30 jours" -> 30)
                const qtyMatch = String(newData.quantity).match(/\d+/);
                const qty = qtyMatch ? parseFloat(qtyMatch[0]) : 0;

                const total_ht = pu * qty;
                newData.total_ht = total_ht.toFixed(2);

                if (withTVA) {
                    const tva = total_ht * 0.19;
                    newData.tva_amount = tva.toFixed(2);
                    newData.total_ttc = (total_ht + tva).toFixed(2);
                } else {
                    newData.total_ttc = total_ht.toFixed(2);
                }
                newData.amount_in_words = numberToWords(parseFloat(newData.total_ttc));

                // Ensure quantity keeps "jours" if it was there or if it's just a number
                if (name === 'quantity' && !newData.quantity.includes('jour')) {
                    newData.quantity = `${newData.quantity} jours`;
                }
            }
            return newData;
        });
    };

    const handlePrint = () => {
        const printContent = printContentRef.current;
        const printWindow = window.open('', '_blank', 'width=800,height=1000');
        if (!printWindow) return;
        const content = printContent.innerHTML;

        printWindow.document.write(`
        <!DOCTYPE html>
        <html>
        <head>
            <title>Facture #${formData.facture_number}</title>
            <meta charset="UTF-8">
            <style>
                @page { margin: 0.5cm; size: A4 portrait; }
                body { 
                    font-family: 'Times New Roman', Times, serif; 
                    margin: 0; 
                    padding: 40px; 
                    color: #000;
                    line-height: 1.2;
                }
                .facture-container { width: 100%; max-width: 800px; margin: 0 auto; }
                
                .header { display: flex; align-items: flex-start; justify-content: space-between; margin-bottom: 40px; border-bottom: 1.5px solid #000; padding-bottom: 20px; }
                .header-left { width: 40%; text-align: left; }
                .header-right { width: 55%; text-align: right; }
                
                .header h1 { margin: 0 0 5px 0; font-size: 24px; font-weight: bold; font-family: sans-serif; }
                .header h2 { margin: 0; font-size: 16px; font-weight: 900; text-transform: uppercase; font-family: sans-serif; }
                .header h2 span.gerant { font-size: 9px; font-weight: bold; text-transform: none; margin-left: 5px; }
                .header .info { font-size: 13px; font-weight: bold; margin-top: 10px; line-height: 1.4; text-align: right; }
                
                .facture-title { text-align: center; margin: 40px 0; font-size: 42px; font-weight: 900; font-family: sans-serif; }
                
                .meta-row { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 30px; }
                .date-part { font-size: 14px; font-weight: bold; }
                
                .client-part { display: flex; align-items: flex-start; }
                .client-part .label { font-size: 18px; font-weight: bold; margin-right: 15px; margin-top: 5px; }
                .client-part .box { 
                    border: 1px solid #000; 
                    padding: 10px 15px; 
                    width: 350px; 
                    min-height: 80px;
                    font-size: 20px;
                    font-weight: 900;
                    text-transform: uppercase;
                    line-height: 1.3;
                    font-family: sans-serif;
                }

                table.main-table { width: 100%; border-collapse: collapse; margin-top: 10px; table-layout: fixed; }
                table.main-table th, table.main-table td { border: 1.5px solid #000; padding: 5px; text-align: center; font-size: 14px; }
                table.main-table th { font-weight: 900; font-size: 15px; text-transform: uppercase; }
                
                .col-n { width: 8%; }
                .col-des { width: 40%; text-align: left !important; padding: 15px !important; }
                .col-u { width: 6%; }
                .col-qty { width: 13%; }
                .col-pu { width: 15%; }
                .col-mt { width: 18%; }

                .content-row { height: 250px; }
                .content-row td { vertical-align: top; padding-top: 20px; }
                .content-row .col-des { vertical-align: top; padding-top: 20px !important; }

                .totals-wrapper { display: flex; justify-content: flex-end; }
                .totals-table { width: 46%; border-collapse: collapse; margin-top: -1.5px; }
                .totals-table td { border: 1.5px solid #000; padding: 4px 10px; font-weight: bold; font-size: 15px; }
                .totals-table .label { text-align: center; width: 40%; background-color: #fff; }
                .totals-table .value { text-align: center; width: 60%; }

                .sum-words-section { margin-top: 40px; font-size: 16px; }
                .sum-words-section .intro { font-weight: normal; margin-bottom: 15px; }
                .sum-words-section .words { font-weight: 900; font-size: 17px; }
                
                .signature-section { text-align: right; margin-top: 60px; padding-right: 40px; }
                .signature-section span { font-weight: 900; font-size: 18px; text-decoration: underline; }

                .editable-input { border: none; background: transparent; font-family: inherit; font-size: inherit; width: 100%; outline: none; }
                
                @media print {
                    .no-print { display: none; }
                    body { padding: 0.5cm; }
                }
            </style>
        </head>
        <body>
            <div class="facture-container">${content}</div>
        </body>
        </html>
        `);

        printWindow.document.close();
        printWindow.onload = function () {
            setTimeout(() => {
                printWindow.focus();
                printWindow.print();
                printWindow.onafterprint = function () { printWindow.close(); };
            }, 500);
        };
    };


    if (isLoading) return <div className="p-8 text-center">Chargement...</div>;

    return (
        <div className="min-h-screen bg-gray-200 p-8 flex justify-center">
            <div className="bg-white shadow-2xl p-12 border border-gray-400" style={{ width: '210mm', minHeight: '297mm' }}>
                {/* Controls */}
                <div className="no-print flex justify-between items-center mb-10 border-b-2 border-dashed pb-6">
                    <h2 className="text-2xl font-black text-gray-800">Facturation Professionnelle</h2>
                    <div className="flex gap-4">
                        <button
                            onClick={() => setIsEditing(!isEditing)}
                            className={`flex items-center gap-2 px-6 py-2 rounded-full font-bold transition-all ${isEditing ? 'bg-green-600 hover:bg-green-700' : 'bg-blue-600 hover:bg-blue-700'} text-white shadow-lg`}
                        >
                            {isEditing ? <Save size={20} /> : <Edit size={20} />}
                            {isEditing ? 'Valider les modifications' : 'Modifier les données'}
                        </button>
                        <button
                            onClick={handlePrint}
                            className="flex items-center gap-2 px-6 py-2 bg-gray-900 hover:bg-black text-white rounded-full font-bold transition-all shadow-lg text-lg"
                        >
                            <Printer size={22} /> IMPRIMER
                        </button>
                        <button onClick={onClose} className="p-2 text-gray-400 hover:text-red-500 transition-colors">
                            <X size={32} />
                        </button>
                    </div>
                </div>

                {/* Facture Content */}
                <div ref={printContentRef} style={{ pointerEvents: isEditing ? 'auto' : 'none' }}>
                   
                    <div style={{ textAlign: 'center', margin: '40px 0', fontSize: '42px', fontWeight: '900', fontFamily: 'sans-serif' }}>
                        Facture : <EditableField name="facture_number" value={formData.facture_number} isEditing={isEditing} onChange={handleInputChange} />
                    </div>

                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '30px' }}>
                        <div style={{ fontSize: '14px', fontWeight: 'bold' }}>
                            Le :<EditableField name="facture_date" value={formData.facture_date} isEditing={isEditing} onChange={handleInputChange} />
                        </div>
                        <div style={{ display: 'flex', alignItems: 'flex-start' }}>
                            <span style={{ fontSize: '18px', fontWeight: 'bold', marginRight: '15px', marginTop: '5px' }}>Doit :</span>
                            <div style={{ border: '1px solid #000', padding: '10px 15px', width: '350px', minHeight: '80px', fontSize: '20px', fontWeight: '900', textTransform: 'uppercase', lineHeight: '1.3', fontFamily: 'sans-serif' }}>
                                <EditableField name="client_name" value={formData.client_name} isEditing={isEditing} isTextArea onChange={handleInputChange} />
                                <br />
                                <EditableField name="client_address" value={formData.client_address} isEditing={isEditing} isTextArea onChange={handleInputChange} />
                            </div>
                        </div>
                    </div>

                    <table className="main-table" style={{ width: '100%', borderCollapse: 'collapse', marginTop: '10px', tableLayout: 'fixed' }}>
                        <thead>
                            <tr>
                                <th style={{ border: '1.5px solid #000', padding: '5px', textAlign: 'center', fontWeight: '900', fontSize: '15px', textTransform: 'uppercase', width: '8%' }}>N°</th>
                                <th style={{ border: '1.5px solid #000', padding: '5px', textAlign: 'left', fontWeight: '900', fontSize: '15px', textTransform: 'uppercase', width: '40%' }}>DESIGNATION</th>
                                <th style={{ border: '1.5px solid #000', padding: '5px', textAlign: 'center', fontWeight: '900', fontSize: '15px', textTransform: 'uppercase', width: '6%' }}>U</th>
                                <th style={{ border: '1.5px solid #000', padding: '5px', textAlign: 'center', fontWeight: '900', fontSize: '15px', textTransform: 'uppercase', width: '13%' }}>Quantité</th>
                                <th style={{ border: '1.5px solid #000', padding: '5px', textAlign: 'center', fontWeight: '900', fontSize: '15px', textTransform: 'uppercase', width: '15%' }}>P.U</th>
                                <th style={{ border: '1.5px solid #000', padding: '5px', textAlign: 'center', fontWeight: '900', fontSize: '15px', textTransform: 'uppercase', width: '18%' }}>MONTANT</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr style={{ height: '250px' }}>
                                <td style={{ border: '1.5px solid #000', padding: '5px', textAlign: 'center', verticalAlign: 'top', paddingTop: '20px' }}>1</td>
                                <td style={{ border: '1.5px solid #000', padding: '15px', textAlign: 'left', verticalAlign: 'top', paddingTop: '20px', fontWeight: 'bold', fontSize: '14px' }}>
                                    <EditableField name="designation" value={formData.designation} isEditing={isEditing} isTextArea onChange={handleInputChange} />
                                </td>
                                <td style={{ border: '1.5px solid #000', padding: '5px', textAlign: 'center', verticalAlign: 'top', paddingTop: '20px' }}>
                                    <EditableField name="unit" value={formData.unit} isEditing={isEditing} onChange={handleInputChange} />
                                </td>
                                <td style={{ border: '1.5px solid #000', padding: '5px', textAlign: 'center', verticalAlign: 'top', paddingTop: '20px' }}>
                                    <EditableField name="quantity" value={formData.quantity} isEditing={isEditing} onChange={handleInputChange} />
                                </td>
                                <td style={{ border: '1.5px solid #000', padding: '5px', textAlign: 'center', verticalAlign: 'top', paddingTop: '20px' }}>
                                    <EditableField name="pu" value={formData.pu} isEditing={isEditing} onChange={handleInputChange} />
                                </td>
                                <td style={{ border: '1.5px solid #000', padding: '5px', textAlign: 'center', verticalAlign: 'top', paddingTop: '20px', fontWeight: 'bold' }}>
                                    {parseFloat(formData.total_ht).toFixed(2).replace('.', ',')}
                                </td>
                            </tr>
                        </tbody>
                    </table>

                    <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <table style={{ width: '46%', borderCollapse: 'collapse', marginTop: '-1.5px' }}>
                            <tbody>
                                <tr>
                                    <td style={{ border: '1.5px solid #000', padding: '4px 10px', fontWeight: 'bold', fontSize: '15px', textAlign: 'center', width: '40%' }}>Total H.T</td>
                                    <td style={{ border: '1.5px solid #000', padding: '4px 10px', fontWeight: 'bold', fontSize: '15px', textAlign: 'center', width: '60%' }}>{parseFloat(formData.total_ht).toFixed(2).replace('.', ',')}</td>
                                </tr>
                                {withTVA && (
                                    <>
                                        <tr>
                                            <td style={{ border: '1.5px solid #000', padding: '4px 10px', fontWeight: 'bold', fontSize: '15px', textAlign: 'center' }}>T.V.A 19%</td>
                                            <td style={{ border: '1.5px solid #000', padding: '4px 10px', fontWeight: 'bold', fontSize: '15px', textAlign: 'center' }}>{parseFloat(formData.tva_amount).toFixed(2).replace('.', ',')}</td>
                                        </tr>
                                        <tr>
                                            <td style={{ border: '1.5px solid #000', padding: '4px 10px', fontWeight: 'bold', fontSize: '15px', textAlign: 'center' }}>Total T.T.C</td>
                                            <td style={{ border: '1.5px solid #000', padding: '4px 10px', fontWeight: 'bold', fontSize: '15px', textAlign: 'center' }}>{parseFloat(formData.total_ttc).toFixed(2).replace('.', ',')}</td>
                                        </tr>
                                    </>
                                )}
                            </tbody>
                        </table>
                    </div>

                    <div style={{ marginTop: '20px', fontSize: '16px' }}>
                        <p style={{ fontWeight: 'normal', marginBottom: '15px' }}>Arrêté la présente facture à la somme de :</p>
                        <p style={{ fontWeight: '900', fontSize: '17px', marginTop: '5px' }}>
                            <EditableField name="amount_in_words" value={formData.amount_in_words} isEditing={isEditing} isTextArea onChange={handleInputChange} /> .
                        </p>
                    </div>

                    <div style={{ textAlign: 'right', marginTop: '60px', paddingRight: '40px' }}>
                        <span style={{ fontWeight: '900', fontSize: '18px', textDecoration: 'underline' }}>Le Transporteur</span>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default FacturePrint;
